import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'X-Session-Token': token } : {};
};

const fmtChange = (n, unit = '') => `${n > 0 ? '+' : ''}${n}${unit}`;

const Stat = ({ label, value, testId, isDarkMode }) => (
  <div className={`flex-1 rounded-xl p-3 text-center ${isDarkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
    <p data-testid={testId} className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
    <p className={`text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
  </div>
);

const ReportDetails = ({ r, isDarkMode }) => {
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const text = isDarkMode ? 'text-gray-200' : 'text-gray-800';
  const bodyRows = Object.entries(r.body || {});
  return (
    <div data-testid="progress-report-details" className="mt-4 space-y-4">
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>Strength gains</p>
        {r.strength_gains?.length ? (
          <ul className="mt-1 space-y-1">
            {r.strength_gains.map((g) => (
              <li key={g.exercise} className={`flex justify-between gap-2 text-sm ${text}`}>
                <span className="truncate">{g.exercise}</span>
                <span className="whitespace-nowrap text-green-600 font-medium">{g.from} → {g.to} lbs ({fmtChange(g.pct, '%')})</span>
              </li>
            ))}
          </ul>
        ) : <p className={`text-sm mt-1 ${muted}`}>No lift increases logged this month yet — keep logging your sets!</p>}
      </div>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${muted}`}>Body changes</p>
        {bodyRows.length ? (
          <ul className="mt-1 space-y-1">
            {bodyRows.map(([field, v]) => (
              <li key={field} className={`flex justify-between text-sm ${text}`}>
                <span className="capitalize">{field}</span>
                <span className="font-medium">{v.start} → {v.end} {field === 'weight' ? 'lbs' : 'in'} ({fmtChange(v.change)})</span>
              </li>
            ))}
          </ul>
        ) : <p className={`text-sm mt-1 ${muted}`}>Log your weight and measurements to see body changes here.</p>}
      </div>
    </div>
  );
};

export const ReportSummary = ({ r, isDarkMode, testPrefix = 'progress-report', defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={`${testPrefix}-card`} className={`rounded-2xl shadow-lg p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            {r.is_preview ? 'Month so far (preview)' : 'Monthly progress report'}
          </p>
          <h3 data-testid={`${testPrefix}-month`} className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{r.label}</h3>
        </div>
        {children}
      </div>
      <p data-testid={`${testPrefix}-headline`} className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{r.headline}</p>
      <div className="flex gap-2 mt-3">
        <Stat label="Workouts" value={`${r.workouts.completed}/${r.workouts.scheduled}`} testId={`${testPrefix}-workouts`} isDarkMode={isDarkMode} />
        <Stat label="New PRs" value={r.prs} testId={`${testPrefix}-prs`} isDarkMode={isDarkMode} />
        <Stat label="Body weight" value={r.body?.weight ? fmtChange(r.body.weight.change, ' lbs') : '—'} testId={`${testPrefix}-weight`} isDarkMode={isDarkMode} />
      </div>
      {open && <ReportDetails r={r} isDarkMode={isDarkMode} />}
      <button data-testid={`${testPrefix}-toggle-btn`} onClick={() => setOpen(!open)}
        className={`mt-3 text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
        {open ? 'Hide details' : 'See full report'}
      </button>
    </div>
  );
};

export const ProgressReportCard = ({ isDarkMode, clientId = null, emptyText = null }) => {
  const [reports, setReports] = useState(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const qs = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
    fetch(`${API_URL}/api/progress-reports${qs}`, { credentials: 'include', headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : { reports: [] }))
      .then((d) => setReports(d.reports || []))
      .catch(() => setReports([]));
  }, [clientId]);

  if (!reports) return null;
  if (!reports.length) {
    return emptyText ? <p data-testid="progress-report-empty" className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{emptyText}</p> : null;
  }
  const r = reports[index];
  return (
    <ReportSummary key={r.id} r={r} isDarkMode={isDarkMode}>
      {reports.length > 1 && (
        <select data-testid="progress-report-month-select" value={index} onChange={(e) => setIndex(Number(e.target.value))}
          className={`text-xs px-2 py-1 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
          {reports.map((rep, i) => <option key={rep.id} value={i}>{rep.label}</option>)}
        </select>
      )}
    </ReportSummary>
  );
};

export const TrainerReportsTab = ({ clientId }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/trainer/clients/${clientId}/progress-report-preview`, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) throw new Error('Could not build the preview');
      setPreview(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="trainer-reports-tab" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-400 flex-1 min-w-[12rem]">Reports are sent to the client on the last day of each month at 6 PM their time.</p>
        <button data-testid="trainer-report-preview-btn" onClick={loadPreview} disabled={loading}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold whitespace-nowrap disabled:opacity-50">
          {loading ? 'Building...' : 'This month so far'}
        </button>
      </div>
      {error && <p data-testid="trainer-report-preview-error" className="text-sm text-red-500">{error}</p>}
      {preview && <ReportSummary key={preview.id} r={preview} isDarkMode={false} testPrefix="trainer-report-preview" defaultOpen />}
      <ProgressReportCard isDarkMode={false} clientId={clientId} emptyText="No monthly reports yet. The first one arrives on the last day of the month." />
    </div>
  );
};
